import React, { useState, useEffect } from "react";
import { baseURL } from "../utils/config";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Box, CircularProgress } from "@material-ui/core";
import { FaMinus, FaPlus } from "react-icons/fa";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdOutlineFileDownload } from "react-icons/md";
import Form from "./Form";
import { Divider } from "@mui/material";

const ProductDetail = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [product, setProduct] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { slug } = useParams();

  const [openSections, setOpenSections] = useState({
    description: true,
    sizes: true,
    specifications: true,
    features: true,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/api/product-details?filters[slug][$eq]=${slug}&populate[Thumbnail]=*&populate[product]=*&populate[Variants][populate][VariantImage]=*&populate[Variants][populate][Attachment]=*&populate[Variants][populate][SizesAvailable]=*&populate[Variants][populate][Specification]=*&populate[Variants][populate][KeyFeatures][populate]=*`
        );
        setProduct(response.data.data[0]);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    getData();
  }, [slug]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [slug]);

  const handleDownload = async ({ pdfUrl, pdfName }) => {
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${pdfName}.pdf`;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleClickEnquiry = (title) => {
    const emailAddress = "salescso@uniklinger.com";
    const subject = `${title}'s Enquiry`;
    const body = `Hey, I wanted to know more about ${title}`;
    const mailtoUrl = `mailto:${encodeURIComponent(
      emailAddress
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  if (isLoading) {
    return (
      <Box
        className="mt-[100px] w-full min-h-[calc(100vh-100px)] flex justify-center items-center"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div
        className="flex mt-[100px] md:mx-40 sm:mx-20 mx-5 mb-40"
        style={{
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Image Side */}
        <div
          className="md:w-2/4 w-full mt-[40px] flex md:justify-end justify-center items-start"
          style={{ padding: "0 6rem" }}
        >
          <img
            src={`${
              baseURL +
              product?.attributes?.Variants[selectedVariant]?.VariantImage?.data
                ?.attributes?.url
            }`}
            alt={
              `${product?.attributes?.Variants[selectedVariant]?.VariantName}` ||
              product?.attributes?.Thumbnail?.data?.attributes?.name
            }
            className="md:max-h-[400px] max-h-[300px] p-20 object-contain"
          />
        </div>

        {/* Content Side */}
        <div className="md:w-2/4 w-full md:pl-10">
          <h1
            className="text-[#015198] text-[32px] font-bold text-left"
            style={{ marginBottom: "-10px", paddingBottom: "0px" }}
          >
            {product?.attributes?.Title}
            <span className="text-[#DB1F51]">
              {" "}
              {/* ({product?.attributes?.Variants[selectedVariant]?.VariantName}) */}
              {product?.attributes?.Variants?.length > 1
                ? `(${product?.attributes?.Variants[selectedVariant]?.VariantName})`
                : ""}
            </span>
          </h1>

          {/* Variants */}
          {product?.attributes?.Variants?.length > 1 && (
            <>
              <h3 className="text-[16px] mb-2 mt-20">Product Variants</h3>
              <div className="flex flex-wrap gap-4 overflow-x-scroll">
                {product?.attributes?.Variants.map((variant, index) => (
                  <div className="flex justify-start flex-col items-start space-y-4 mt-[20px]">
                    <img
                      key={index}
                      src={`${baseURL}${variant?.VariantImage?.data?.attributes?.url}`}
                      alt={variant?.VariantName}
                      className="w-[100px] h-[100px] cursor-pointer transition-all duration-200 object-contain"
                      style={{
                        border:
                          selectedVariant === index
                            ? "2px solid #A53B71"
                            : "1px solid #939393",
                      }}
                      onClick={() => setSelectedVariant(index)}
                    />
                    <h1 className="text-[12px]">{variant?.VariantName}</h1>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mt-10 max-w-[560px]">
            {/* Description */}
            <h2
              className="text-[16px] flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("description")}
              style={{ padding: "0px" }}
            >
              PRODUCT DESCRIPTION{" "}
              {openSections.description ? <FaMinus /> : <FaPlus />}
            </h2>
            {openSections.description && (
              <p className="text-[14px]">
                {product?.attributes?.Variants[selectedVariant]?.Description}
              </p>
            )}

            <Divider />
            {/* Sizes */}
            {product?.attributes?.Variants[
              selectedVariant
            ]?.SizesAvailable?.some((size) => size?.Title) && (
              <>
                <h2
                  className="text-[16px] flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection("sizes")}
                  style={{ padding: "0px" }}
                >
                  SIZES AVAILABLE{" "}
                  {openSections.sizes ? <FaMinus /> : <FaPlus />}
                </h2>

                {openSections.sizes && (
                  <div className="flex flex-col" style={{ gap: "10px" }}>
                    <div
                      className="flex flex-wrap md:gap-[40px]"
                      style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: "10px",
                      }}
                    >
                      {product?.attributes?.Variants[
                        selectedVariant
                      ]?.SizesAvailable.map(
                        (sizes, idx) =>
                          sizes?.Title && (
                            <p key={idx} className="flex flex-col gap-2">
                              <span className="text-[#9F9F9F]">
                                {sizes?.Title}
                              </span>
                              {sizes?.Text}
                            </p>
                          )
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <Divider />
            {/* Specification */}
            {product?.attributes?.Variants[
              selectedVariant
            ]?.Specification?.some((spec) => spec?.Title) && (
              <>
                <h2
                  className="text-[16px] flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection("specifications")}
                  style={{ padding: "0px" }}
                >
                  SPECIFICATION{" "}
                  {openSections.specifications ? <FaMinus /> : <FaPlus />}
                </h2>

                {openSections.specifications && (
                  <div>
                    {product?.attributes?.Variants[
                      selectedVariant
                    ]?.Specification.map(
                      (specification, idx) =>
                        specification?.Title && (
                          <p key={idx}>
                            <b>{specification?.Title}</b> {specification?.Text}
                          </p>
                        )
                    )}
                  </div>
                )}
              </>
            )}

            <Divider />
            {/* Key Features */}
            {product?.attributes?.Variants[selectedVariant]?.KeyFeatures?.some(
              (feature) => feature?.Title
            ) && (
              <>
                <h2
                  className="text-[16px] flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection("features")}
                  style={{ padding: "0px" }}
                >
                  KEY FEATURES{" "}
                  {openSections.features ? <FaMinus /> : <FaPlus />}
                </h2>

                {openSections.features && (
                  <div
                    className="gap-10 mb-10 flex-wrap"
                    style={{
                      display: "flex",
                      alignItems: "start",
                      flexDirection: !isMobile ? "row" : "column",
                    }}
                  >
                    {product?.attributes?.Variants[
                      selectedVariant
                    ]?.KeyFeatures.map(
                      (feature, index) =>
                        feature?.Title && (
                          <div
                            key={index}
                            className={`${
                              !isMobile ? "max-w-[14rem]" : "w-full"
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "start",
                              flexDirection: isMobile ? "row" : "column",
                            }}
                          >
                            {feature?.Image?.data?.attributes?.url && (
                              <img
                                src={`${baseURL}${feature?.Image?.data?.attributes?.url}`}
                                alt={feature?.Title}
                                className="shadow-lg p-2 rounded-sm bg-cover"
                                style={{
                                  width: "96px",
                                  height: "96px",
                                  marginRight: isMobile ? "10px" : "0px",
                                }}
                              />
                            )}
                            {feature?.Title && (
                              <span className="text-[14px] mt-4">
                                {feature?.Title}
                              </span>
                            )}
                          </div>
                        )
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex gap-5 mt-10">
            {product?.attributes?.Variants[selectedVariant]?.Attachment?.data
              ?.attributes?.url && (
              <button
                onClick={() =>
                  handleDownload({
                    pdfUrl: `${baseURL}${product?.attributes?.Variants[selectedVariant]?.Attachment?.data?.attributes?.url}`,
                    pdfName:
                      product?.attributes?.Variants[selectedVariant]?.Attachment
                        ?.data?.attributes?.name,
                  })
                }
                className="rounded-full text-[20px] px-16 py-4 flex justify-start items-center gap-2 font-bold"
                style={{
                  border: "1px solid #E1345D",
                  fontSize: isMobile ? "14px" : "20px",
                }}
              >
                Download <MdOutlineFileDownload />
              </button>
            )}
            <button
              onClick={() => handleClickEnquiry(product?.attributes?.Title)}
              style={{
                fontSize: isMobile ? "14px" : "20px",
                backgroundImage:
                  "linear-gradient(-240deg, #2b9cff 11%, #0066c1 33%, #00417b 68%, #c33b6f 83%)",
              }}
              className="text-[20px] text-white flex justify-start items-center gap-2 rounded-full px-16 py-4 font-bold"
            >
              Enquire Now <FaArrowRightLong size={20} className="pl-2" />
            </button>
          </div>
        </div>
      </div>
      <Form />
    </>
  );
};

export default ProductDetail;
